/* eslint-disable max-lines */
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import cn from 'classnames';

import Button from '@components/atoms/ui-kit/button';
import { Skeleton } from '@components/atoms/ui-kit/skeleton';
import { EmptyState } from '@components/atoms/ui-kit/empty-state';
import Modal from '@components/atoms/modals/modal';
import Input from '@components/atoms/ui-kit/input';
import Switch from '@components/atoms/ui-kit/switch';
import DataGridV2, {
  type DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';

import { useGetLicenseRequests } from '@utils/api/resellers/use-get-license-requests';
import { useCreateLicenseRequest } from '@utils/api/resellers/use-create-license-request';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import {
  LicenseRequest,
  LicenseRequestStatus,
  CreateLicenseRequestPayload
} from '@utils/types/resellers/license-request';
import { Plus, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

/* eslint-disable no-restricted-syntax -- Semantic colors for status badges */
const STATUS_COLORS: Record<
  LicenseRequestStatus,
  { bg: string; text: string }
> = {
  pending: { bg: 'rgba(245, 158, 11, 0.15)', text: '#D97706' },
  approved: { bg: 'rgba(16, 185, 129, 0.15)', text: '#059669' },
  rejected: { bg: 'rgba(239, 68, 68, 0.15)', text: '#DC2626' }
};
/* eslint-enable no-restricted-syntax */

const STATUS_ICONS: Record<LicenseRequestStatus, typeof Clock> = {
  pending: Clock,
  approved: CheckCircle,
  rejected: XCircle
};

type Row = {
  id: number;
  org_limit: number;
  user_limit: number;
  max_processing: number;
  status: LicenseRequestStatus;
  notes: string | null;
  rejection_reason: string | null;
  created_at: string;
};

type Props = { resellerId?: string };

interface FormData {
  org_limit: number;
  user_limit: number;
  max_processing?: number;
  notes?: string;
  realtime?: boolean;
  max_realtime_streams?: number | null;
  realtime_languages?: string; // csv input — split before posting
}

export const LicenseRequestsDetails = ({ resellerId }: Props) => {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: requestsData, isLoading } = useGetLicenseRequests(
    resellerId || ''
  );
  const createLicenseRequest = useCreateLicenseRequest();

  // Check if there's already a pending request
  const hasPendingRequest = useMemo(() => {
    return (
      requestsData?.result?.some(
        (req: LicenseRequest) => req.status === 'pending'
      ) ?? false
    );
  }, [requestsData]);

  const validationSchema = Yup.object().shape({
    org_limit: Yup.number()
      .min(1, t('min-value-is-1'))
      .required(t('org-limit-required')),
    user_limit: Yup.number()
      .min(1, t('min-value-is-1'))
      .required(t('user-limit-required')),
    max_processing: Yup.number().min(1, t('min-value-is-1')),
    notes: Yup.string().max(2000, t('max-2000-characters')),
    realtime: Yup.boolean(),
    max_realtime_streams: Yup.number()
      .nullable()
      .transform(v => (Number.isNaN(v) || v === undefined ? null : v))
      .when('realtime', {
        is: true,
        then: schema =>
          schema
            .min(1, t('min-value-is-1'))
            .required(t('max-realtime-streams-required'))
      }),
    realtime_languages: Yup.string()
  });

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors }
  } = useForm<FormData>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      org_limit: 1,
      user_limit: 10,
      max_processing: 3,
      notes: '',
      realtime: false,
      max_realtime_streams: null,
      realtime_languages: ''
    }
  });

  const realtimeEnabled = watch('realtime');

  const onSubmit = async (data: FormData) => {
    if (!resellerId) return;

    setIsSubmitting(true);
    try {
      const realtimeLanguages = data.realtime_languages
        ? data.realtime_languages
            .split(',')
            .map(s => s.trim())
            .filter(Boolean)
        : null;

      const payload: CreateLicenseRequestPayload = {
        org_limit: data.org_limit,
        user_limit: data.user_limit,
        max_processing: data.max_processing || 3,
        notes: data.notes || undefined,
        realtime: data.realtime ?? false,
        max_realtime_streams: data.realtime
          ? (data.max_realtime_streams ?? null)
          : null,
        realtime_languages: data.realtime ? realtimeLanguages : null
      };

      await createLicenseRequest.mutateAsync({
        customerId: resellerId,
        payload
      });

      setShowCreateModal(false);
      reset();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowCreateModal(false);
    reset();
  };

  const columns: DataGridColumn<Row>[] = [
    {
      id: 'id',
      header: t('request-id'),
      accessorKey: 'id',
      maxWidth: 100,
      cell: ({ getValue }) => `#${getValue()}`
    },
    {
      id: 'limits',
      header: t('limits'),
      accessorKey: 'org_limit',
      minWidth: 140,
      cell: ({ row }) => (
        <span className="text-sm">
          {row.original.org_limit} {t('orgs')} / {row.original.user_limit}{' '}
          {t('users')}
        </span>
      )
    },
    {
      id: 'max_processing',
      header: t('processing'),
      accessorKey: 'max_processing',
      maxWidth: 100,
      cell: ({ getValue }) => `${getValue()} ${t('slots')}`
    },
    {
      id: 'status',
      header: t('status'),
      accessorKey: 'status',
      minWidth: 120,
      cell: ({ getValue }) => {
        const status = getValue() as LicenseRequestStatus;
        const colors = STATUS_COLORS[status];
        const Icon = STATUS_ICONS[status];
        return (
          <span
            className={cn(
              'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium'
            )}
            style={{
              backgroundColor: colors.bg,
              color: colors.text
            }}
          >
            <Icon className="h-3 w-3" />
            {t(status)}
          </span>
        );
      }
    },
    {
      id: 'notes',
      header: t('notes'),
      accessorKey: 'notes',
      minWidth: 200,
      cell: ({ getValue }) => {
        const notes = getValue() as string | null;
        if (!notes) return '-';
        return (
          <span className="text-xs truncate block max-w-[200px]" title={notes}>
            {notes}
          </span>
        );
      }
    },
    {
      id: 'rejection_reason',
      header: t('rejection-reason'),
      accessorKey: 'rejection_reason',
      minWidth: 200,
      cell: ({ getValue, row }) => {
        if (row.original.status !== 'rejected') return '-';
        const reason = getValue() as string | null;
        if (!reason) return '-';
        return (
          <span
            className="text-xs truncate block max-w-[200px]"
            style={{ color: STATUS_COLORS.rejected.text }}
            title={reason}
          >
            {reason}
          </span>
        );
      }
    },
    {
      id: 'created_at',
      header: t('created'),
      accessorKey: 'created_at',
      minWidth: 140,
      cell: ({ getValue }) => {
        const date = getValue() as string;
        if (!date) return '-';
        try {
          return formatDistanceToNow(new Date(date), { addSuffix: true });
        } catch {
          return '-';
        }
      }
    }
  ];

  const rows: Row[] = useMemo(() => {
    const list = requestsData?.result ?? [];
    return list.map((req: LicenseRequest) => ({
      id: req.id,
      org_limit: req.org_limit,
      user_limit: req.user_limit,
      max_processing: req.max_processing,
      status: req.status,
      notes: req.notes,
      rejection_reason: req.rejection_reason,
      created_at: req.created_at
    }));
  }, [requestsData]);

  return (
    <div className="p-4 space-y-4">
      {/* Header with Request Button */}
      <div className="flex justify-between items-center">
        <p className="text-sm" style={{ color: getColour('inactiveText') }}>
          {t('view-and-manage-license-requests-for-customer')}
        </p>
        <div className="flex items-center gap-2">
          {hasPendingRequest && (
            <span
              className="flex items-center gap-1 text-xs px-2 py-1 rounded-2xl"
              style={{
                backgroundColor: STATUS_COLORS.pending.bg,
                color: STATUS_COLORS.pending.text
              }}
            >
              <AlertCircle className="h-3 w-3" />
              {t('request-pending-exists')}
            </span>
          )}
          <Button
            onClick={() => setShowCreateModal(true)}
            icon={<Plus width={14} />}
            size="small"
            label={t('request-license')}
            variant="secondary"
            className="w-fit"
            disabled={hasPendingRequest}
          />
        </div>
      </div>

      {/* Create License Request Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={handleCloseModal}
        size="small"
        title={t('request-on-prem-license')}
        caption={t('submit-license-request-for-customer')}
        footerActions={[
          <Button
            key="cancel"
            onClick={handleCloseModal}
            label={t('cancel')}
            size="small"
            variant="smoke"
            disabled={isSubmitting}
          />,
          <Button
            key="submit"
            onClick={handleSubmit(onSubmit)}
            label={isSubmitting ? t('submitting') : t('submit-request')}
            size="small"
            variant="primary"
            disabled={isSubmitting}
          />
        ]}
      >
        <form className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Input
              id="org_limit"
              label={
                <>
                  {t('org-limit')} <span className="text-red-500">*</span>
                </>
              }
              type="number"
              min={1}
              size="small"
              {...register('org_limit', { valueAsNumber: true })}
              error={errors.org_limit?.message}
              placeholder="5"
            />
            <Input
              id="user_limit"
              label={
                <>
                  {t('user-limit')} <span className="text-red-500">*</span>
                </>
              }
              type="number"
              min={1}
              size="small"
              {...register('user_limit', { valueAsNumber: true })}
              error={errors.user_limit?.message}
              placeholder="50"
            />
          </div>

          <Input
            id="max_processing"
            label={t('max-processing')}
            type="number"
            min={1}
            size="small"
            {...register('max_processing', { valueAsNumber: true })}
            error={errors.max_processing?.message}
            placeholder="3"
          />
          <p
            className="text-xs -mt-2"
            style={{ color: getColour('inactiveText') }}
          >
            {t('concurrent-transcription-slots')}
          </p>

          <Input
            id="notes"
            label={t('notes')}
            multiline
            size="small"
            {...register('notes')}
            error={errors.notes?.message}
            placeholder={t('optional-notes-for-scriptix-team')}
          />

          <div
            className="rounded-2xl border p-3 space-y-3"
            style={{ borderColor: getColour('border') }}
          >
            <Switch
              id="realtime"
              label={t('realtime-asr-add-on')}
              register={register('realtime')}
            />
            <p className="text-xs" style={{ color: getColour('inactiveText') }}>
              {t('realtime-asr-help')}
            </p>

            {realtimeEnabled ? (
              <div className="space-y-3">
                <Input
                  id="max_realtime_streams"
                  label={
                    <>
                      {t('max-realtime-streams')}{' '}
                      <span className="text-red-500">*</span>
                    </>
                  }
                  type="number"
                  min={1}
                  size="small"
                  {...register('max_realtime_streams', {
                    setValueAs: v =>
                      v === '' || v === null || v === undefined
                        ? null
                        : Number(v)
                  })}
                  error={errors.max_realtime_streams?.message}
                  placeholder="4"
                />
                <Input
                  id="realtime_languages"
                  label={t('realtime-languages')}
                  size="small"
                  {...register('realtime_languages')}
                  error={errors.realtime_languages?.message}
                  placeholder="nl-nl, en-us"
                />
                <p
                  className="text-xs -mt-2"
                  style={{ color: getColour('inactiveText') }}
                >
                  {t('realtime-languages-help')}
                </p>
              </div>
            ) : null}
          </div>
        </form>
      </Modal>

      {/* License Requests Table */}
      {isLoading ? (
        <Skeleton variant="table" rows={5} />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<AlertCircle className="h-7 w-7" />}
          title={t('no-license-requests')}
          subtitle={t('click-request-license-to-get-started')}
        />
      ) : (
        <div className="pt-2">
          <DataGridV2<Row>
            data={rows}
            columns={columns}
            enableRowSelection={false}
            enablePagination
            manualPagination={false}
            enableSorting
            manualSorting={false}
            enableFiltering={false}
            manualFiltering={false}
            totalItems={requestsData?.total_results ?? 0}
            pageSize={10}
            pageSizeOptions={[10, 25, 50]}
            showColumnFilters={false}
            filterMode="popover"
            height="55vh"
            dense
            grid={false}
            striped
            loading={isLoading}
            getRowId={row => String(row.id)}
          />
        </div>
      )}
    </div>
  );
};
